import _ from 'lodash';

// import { IStateful } from '../base/app/types';
import { getCurrentConference } from '../base/conference/functions';
import {
    getLocalParticipant,
    getParticipantById,
    getParticipantCount,
    isLocalParticipantModerator
} from '../base/participants/functions';
import { toState } from '../base/redux/functions';

import { BREAKOUT_ROOMS_RENAME_FEATURE, FEATURE_KEY } from './constants';

/**
 * Returns the rooms object for breakout rooms.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {Object} Object of rooms.
 */
export const getBreakoutRooms = stateful => toState(stateful)[FEATURE_KEY].rooms;

/**
 * Returns the total participant count for the meeting based on redux state only.
 * It aggregates unique participants across breakout rooms and falls back to
 * current room participants when breakout room data is not available yet.
 *
 * @param {Object} stateful - The redux store, getState function, or redux state.
 * @returns {number}
 */
export const getBreakoutRoomMeetingParticipantCount = stateful => {
    const state = toState(stateful);
    const rooms = getBreakoutRooms(state) || {};
    const participantIds = new Set();

    Object.values(rooms).forEach(room => {
        const participants = room?.participants || {};

        Object.keys(participants).forEach(participantJid => {
            const endpointId = participantJid.includes('/')
                ? participantJid.substring(participantJid.indexOf('/') + 1)
                : participantJid;

            if (endpointId) {
                participantIds.add(endpointId);
            }
        });
    });

    if (participantIds.size > 0) {
        return participantIds.size;
    }

    return 0;

};

/**
 * Returns the main room.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {Object|undefined} The main room object, or undefined.
 */
export const getMainRoom = (stateful: Object) => {
    const rooms = getBreakoutRooms(stateful);

    return _.find(rooms, room => Boolean(room.isMainRoom));
};

/**
 * Returns the rooms info.
 *
 * @param {Object} stateful - The redux store, the redux.

* @returns {Object} The rooms info.
 */
export const getRoomsInfo = (stateful: Object) => {
    const breakoutRooms = getBreakoutRooms(stateful);
    const conference = getCurrentConference(stateful);

    const initialRoomsInfo = {
        rooms: []
    };

    // only main roomn
    if (!breakoutRooms || Object.keys(breakoutRooms).length === 0) {
        // filter out hidden participants
        const conferenceParticipants = conference?.getParticipants()
            .filter(participant => !participant.isHidden());

        const localParticipant = getLocalParticipant(stateful);
        let localParticipantInfo;

        if (localParticipant) {
            localParticipantInfo = {
                role: localParticipant.role,
                displayName: localParticipant.name,
                avatarUrl: localParticipant.loadableAvatarUrl,
                id: localParticipant.id
            };
        }

        return {
            ...initialRoomsInfo,
            rooms: [ {
                isMainRoom: true,
                id: conference?.room?.roomjid,
                jid: conference?.room?.myroomjid,
                participants: conferenceParticipants?.length > 0
                    ? [
                        localParticipantInfo,
                        ...conferenceParticipants.map(participantItem => {
                            const storeParticipant = getParticipantById(stateful, participantItem.getId());

                            return {
                                jid: participantItem.getJid(),
                                role: participantItem.getRole(),
                                displayName: participantItem.getDisplayName(),
                                avatarUrl: storeParticipant?.loadableAvatarUrl,
                                id: participantItem.getId()
                            };
                        }) ]
                    : [ localParticipantInfo ]
            } ]
        };
    }

    return {
        ...initialRoomsInfo,
        rooms: Object.keys(breakoutRooms).map(breakoutRoomKey => {
            const breakoutRoomItem = breakoutRooms[breakoutRoomKey];

            return {
                isMainRoom: Boolean(breakoutRoomItem.isMainRoom),
                id: breakoutRoomItem.id,
                jid: breakoutRoomItem.jid,
                participants: breakoutRoomItem.participants && Object.keys(breakoutRoomItem.participants).length
                    ? Object.keys(breakoutRoomItem.participants).map(participantLongId => {
                        const participantItem = breakoutRoomItem.participants[participantLongId];
                        const ids = participantLongId.split('/');
                        const storeParticipant = getParticipantById(stateful,
                            ids.length > 1 ? ids[1] : participantItem.jid);

                        return {
                            jid: participantItem?.jid,
                            role: participantItem?.role,
                            displayName: participantItem?.displayName,
                            avatarUrl: storeParticipant?.loadableAvatarUrl,
                            id: storeParticipant ? storeParticipant.id
                                : participantLongId
                        };
                    }) : []
            };
        })
    };
};

/**
 * Returns the room by Jid.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @param {string} roomJid - The jid of the room.
 * @returns {Object|undefined} The main room object, or undefined.
 */
export const getRoomByJid = (stateful: Object, roomJid: string) => {
    const rooms = getBreakoutRooms(stateful);

    return _.find(rooms, room => room.jid === roomJid);
};

/**
 * Returns the id of the current room.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {string} Room id or undefined.
 */
export const getCurrentRoomId = (stateful: Object) => {
    const conference = getCurrentConference(stateful);

    return conference?.getName();
};

/**
 * Determines whether the local participant is in a breakout room.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {boolean}
 */
export const isInBreakoutRoom = (stateful: Object) => {
    const conference = getCurrentConference(stateful);

    return conference?.getBreakoutRooms()
        ?.isBreakoutRoom();
};

/**
 * Returns the breakout rooms config.
 *
 * @param {Object} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {Object}
 */
export const getBreakoutRoomsConfig = (stateful: Object) => {
    const state = toState(stateful);
    const { breakoutRooms = {} } = state['features/base/config'];

    return breakoutRooms;
};

/**
 * Returns whether one more breakout room may be created. Counts the rooms the
 * server has confirmed plus the creations still on their way to it: the room
 * list only grows when the server's (throttled) update arrives, so without the
 * pending ones every click made before that update would pass the limit.
 *
 * @param {Object} stateful - Global state.
 * @returns {boolean}
 */
export const isBreakoutRoomCreationAllowed = (stateful: Object) => {
    const state = toState(stateful);
    const { rooms, roomCounter = 0, serverRoomCounter = 0 } = state[FEATURE_KEY];
    const { confSettings } = state['features/base/jwt'];

    // The main room is part of the list.
    const confirmedRooms = Math.max(_.size(rooms) - 1, 0);
    const pendingRooms = Math.max(roomCounter - serverRoomCounter, 0);

    return confirmedRooms + pendingRooms < (confSettings?.BREAKOUT_ROOMS_ALLOWED ?? 3);
};

/**
 * Returns whether the add breakout room button is visible.
 *
 * @param {Object} stateful - Global state.
 * @returns {boolean}
 */
export const isAddBreakoutRoomButtonVisible = (stateful: Object, isNative: Boolean = false) => {
    const state = toState(stateful);
    const isLocalModerator = isLocalParticipantModerator(state);
    const { conference } = state['features/base/conference'];
    const isBreakoutRoomsSupported = conference?.getBreakoutRooms()?.isSupported();

    // const { hideAddRoomButton } = getBreakoutRoomsConfig(state);
    const { confSettings } = state['features/base/jwt'];
    const isBreakroomCreationAllowed = isBreakoutRoomCreationAllowed(state);

    return isNative === false ? isLocalModerator && isBreakoutRoomsSupported
        && confSettings?.ENABLE_BREAKOUT_ROOMS && isBreakroomCreationAllowed
        : isBreakoutRoomsSupported && confSettings?.ENABLE_BREAKOUT_ROOMS && isLocalModerator ? true : (_.size(conference?.getBreakoutRooms()?._rooms) - 1) > 0;
};

/**
 * Returns whether the auto assign participants to breakout rooms button is visible.
 *
 * @param {Object} stateful - Global state.
 * @returns {boolean}
 */
export const isAutoAssignParticipantsVisible = (stateful: Object) => {
    const state = toState(stateful);
    const rooms = getBreakoutRooms(state);
    const inBreakoutRoom = isInBreakoutRoom(state);
    const isLocalModerator = isLocalParticipantModerator(state);
    const participantsCount = getParticipantCount(state);
    const { hideAutoAssignButton } = getBreakoutRoomsConfig(state);

    return !inBreakoutRoom
        && isLocalModerator
        && participantsCount > 2
        && Object.keys(rooms).length > 1
        && !hideAutoAssignButton;
};


/**
 * Returns true if renaming the currently joined breakout room is allowed and false otherwise.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} - True if reanming the currently joined breakout room is allowed and false otherwise.
 */
export function isCurrentRoomRenamable(state: Object) {
    return isInBreakoutRoom(state) && isBreakoutRoomRenameAllowed(state);
}

/**
 * Returns true if renaming a breakout room is allowed and false otherwise.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} - True if renaming a breakout room is allowed and false otherwise.
 */
export function isBreakoutRoomRenameAllowed(state: Object) {
    const isLocalModerator = isLocalParticipantModerator(state);
    const conference = getCurrentConference(state);
    const isRenameBreakoutRoomsSupported
            = true || conference?.getBreakoutRooms()?.isFeatureSupported(BREAKOUT_ROOMS_RENAME_FEATURE);

    return isLocalModerator && isRenameBreakoutRoomsSupported;
}

